/**
 * Toast Notification System
 * A lightweight, accessible toast notification component
 */

const Toast = {
  container: null,
  queue: [],

  // Toast types with their styling
  types: {
    success: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>`
    },
    error: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>`
    },
    info: {
      bgColor: 'bg-violet-50',
      borderColor: 'border-violet-200',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>`
    },
    warning: {
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>`
    },
    loading: {
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="animate-spin">
        <line x1="12" y1="2" x2="12" y2="6"></line>
        <line x1="12" y1="18" x2="12" y2="22"></line>
        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
        <line x1="2" y1="12" x2="6" y2="12"></line>
        <line x1="18" y1="12" x2="22" y2="12"></line>
        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
        <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
      </svg>`
    }
  },

  /**
   * Initialize the toast container
   */
  init() {
    if (this.container) return;

    this.container = document.createElement('div');
    this.container.id = 'toast-container';
    this.container.className = 'fixed top-4 right-4 z-[99999] flex flex-col gap-3 max-w-sm w-full pointer-events-none';
    this.container.setAttribute('aria-live', 'polite');
    this.container.setAttribute('aria-atomic', 'true');
    document.body.appendChild(this.container);

    // Add animation styles
    this.addStyles();
  },

  /**
   * Add CSS animations
   */
  addStyles() {
    if (document.getElementById('toast-styles')) return;

    const style = document.createElement('style');
    style.id = 'toast-styles';
    style.textContent = `
      @keyframes toast-slide-in {
        from {
          opacity: 0;
          transform: translateX(100%);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes toast-slide-out {
        from {
          opacity: 1;
          transform: translateX(0);
        }
        to {
          opacity: 0;
          transform: translateX(100%);
        }
      }

      .toast-enter {
        animation: toast-slide-in 0.3s ease-out forwards;
      }

      .toast-exit {
        animation: toast-slide-out 0.2s ease-in forwards;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .animate-spin {
        animation: spin 1s linear infinite;
      }
    `;
    document.head.appendChild(style);
  },

  /**
   * Show a toast notification
   * @param {string} message - Main message to display
   * @param {string} type - Type of toast (success, error, info, warning, loading)
   * @param {Object} options - Additional options
   * @param {string} options.description - Optional secondary text
   * @param {number} options.duration - Duration in ms (0 for persistent)
   * @param {string} options.id - Optional unique ID for the toast
   * @returns {string} Toast ID for programmatic dismissal
   */
  show(message, type = 'info', options = {}) {
    this.init();

    const {
      description = '',
      duration = type === 'loading' ? 0 : 4000,
      id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    } = options;

    const typeConfig = this.types[type] || this.types.info;

    // Create toast element
    const toast = document.createElement('div');
    toast.id = id;
    toast.className = `toast-enter pointer-events-auto rounded-xl ${typeConfig.bgColor} border ${typeConfig.borderColor} shadow-lg p-4 flex items-start gap-3`;
    toast.setAttribute('role', 'alert');

    toast.innerHTML = `
      <div class="flex-shrink-0 w-8 h-8 rounded-full ${typeConfig.iconBg} ${typeConfig.iconColor} flex items-center justify-center">
        ${typeConfig.icon}
      </div>
      <div class="flex-1 min-w-0">
        <p class="font-medium text-slate-900 text-sm">${this.escapeHtml(message)}</p>
        ${description ? `<p class="text-sm text-slate-600 mt-0.5">${this.escapeHtml(description)}</p>` : ''}
      </div>
      <button
        type="button"
        class="flex-shrink-0 w-6 h-6 rounded-full hover:bg-slate-200/50 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
        aria-label="Dismiss notification"
        onclick="Toast.dismiss('${id}')"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    `;

    this.container.appendChild(toast);

    // Auto dismiss after duration (if not 0)
    if (duration > 0) {
      setTimeout(() => {
        this.dismiss(id);
      }, duration);
    }

    return id;
  },

  /**
   * Dismiss a toast by ID
   * @param {string} id - Toast ID to dismiss
   */
  dismiss(id) {
    const toast = document.getElementById(id);
    if (!toast) return;

    toast.classList.remove('toast-enter');
    toast.classList.add('toast-exit');

    // Remove after animation
    setTimeout(() => {
      toast.remove();
    }, 200);
  },

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    if (!this.container) return;

    const toasts = this.container.querySelectorAll('[id^="toast-"]');
    toasts.forEach(toast => {
      this.dismiss(toast.id);
    });
  },

  /**
   * Update an existing toast
   * @param {string} id - Toast ID to update
   * @param {string} message - New message
   * @param {string} type - New type
   * @param {Object} options - New options
   */
  update(id, message, type, options = {}) {
    this.dismiss(id);
    return this.show(message, type, { ...options, id });
  },

  /**
   * Convenience methods
   */
  success(message, options = {}) {
    return this.show(message, 'success', options);
  },

  error(message, options = {}) {
    return this.show(message, 'error', options);
  },

  info(message, options = {}) {
    return this.show(message, 'info', options);
  },

  warning(message, options = {}) {
    return this.show(message, 'warning', options);
  },

  loading(message, options = {}) {
    return this.show(message, 'loading', options);
  },

  /**
   * Promise-based toast for async operations
   * @param {Promise} promise - Promise to track
   * @param {Object} messages - Messages for each state
   * @returns {Promise} The original promise
   */
  async promise(promise, messages = {}) {
    const {
      loading = 'Loading...',
      success = 'Success!',
      error = 'Something went wrong'
    } = messages;

    const toastId = this.loading(loading);

    try {
      const result = await promise;
      this.update(toastId, success, 'success');
      return result;
    } catch (err) {
      this.update(toastId, error, 'error');
      throw err;
    }
  },

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
};

// Make Toast available globally
if (typeof window !== 'undefined') {
  window.Toast = Toast;
}

export default Toast;
